// FirmwareUploadView – choose a firmware file and upload it to the fleet.

import React, { JSX, useState } from 'react';
import { dispatchToast, dispatchToastError } from '../EventsManagementView/utils';
import InlineSpinner from '../shared/InlineSpinner';

type FirmwareUploadPanelProps = {
    onUploaded?: () => Promise<void> | void;
};

// ---- Component ----

export default function FirmwareUploadPanel(props: FirmwareUploadPanelProps): JSX.Element {
    const { onUploaded } = props;
    const [fileName, setFileName] = useState<string | null>(null);
    const [filePath, setFilePath] = useState<string | null>(null);
    const [firmwareVersion, setFirmwareVersion] = useState('');
    const [role, setRole] = useState<'rear' | 'front'>('rear');
    const [busy, setBusy] = useState(false);

    // Native file picker; we keep both path (for upload) and display name.
    async function handleChooseFile(): Promise<void> {
        const res = await window.fleetAPI.openFileDialog();

        if (res?.canceled) return;

        const path = res?.filePaths?.[0];
        if (!path) return;

        setFilePath(path);
        setFileName(path.split(/[\\/]/).pop() ?? null);
    }

    // Send file to backend; clear selection on success.
    async function handleUpload(): Promise<void> {
        if (!filePath) {
            dispatchToast('toast-error', 'No firmware file selected');
            return;
        }
        const version = firmwareVersion.trim();
        if (!version) {
            dispatchToast('toast-error', 'Enter the firmware version (must match device-reported version)');
            return;
        }

        setBusy(true);

        try {
            const res = await window.eventsAPI.uploadFirmware({
                filePath,
                firmwareVersion: version,
                role,
            });

            if (!res.ok) {
                dispatchToast(
                    'toast-error',
                    res.error || 'Firmware upload blocked',
                );
                return;
            }

            dispatchToast(
                'toast-info',
                `Firmware uploaded: ${fileName}`,
            );

            // Refresh any dependent UI (e.g. the "Firmware versions" table).
            try {
                await onUploaded?.();
            } catch (err: any) {
                dispatchToastError(err, 'Failed to refresh firmware versions');
            }

            setFileName(null);
            setFilePath(null);
            setFirmwareVersion('');
        } catch (err: any) {
            dispatchToastError(err);
        } finally {
            setBusy(false);
        }
    }

    function handleClear(): void {
        setFileName(null);
        setFilePath(null);
        setFirmwareVersion('');
        setRole('rear');
    }

    return (
        <div className="px-4 py-3 text-[11px] space-y-3">
            {/* Choose file, show name, upload */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                    type="button"
                    onClick={handleChooseFile}
                    disabled={busy}
                    className="inline-flex items-center justify-center rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-[11px] font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-50"
                >
                    Choose file
                </button>

                <div className="flex-1 rounded-md border border-dashed border-gray-300 dark:border-slate-600 bg-gray-50 dark:bg-slate-800 px-3 py-1.5 text-[11px]">
                    {fileName ? (
                        <div className="flex items-center justify-between gap-2">
                            <span
                                className="truncate text-gray-800 dark:text-slate-200"
                                title={fileName}
                            >
                                {fileName}
                            </span>
                            <button
                                type="button"
                                onClick={handleClear}
                                disabled={busy}
                                className="text-[11px] font-medium text-gray-500 hover:text-gray-700 dark:text-slate-300 disabled:opacity-50"
                            >
                                Clear
                            </button>
                        </div>
                    ) : (
                        <span className="text-gray-400 dark:text-slate-400">
                            No file selected
                        </span>
                    )}
                </div>

                <button
                    type="button"
                    onClick={handleUpload}
                    disabled={!filePath || busy || !firmwareVersion.trim()}
                    className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
                >
                    {busy ? (
                        <>
                            <InlineSpinner size="xs" variant="current" />
                            Uploading…
                        </>
                    ) : (
                        'Upload firmware'
                    )}
                </button>
            </div>
            <label className="block text-[11px] text-gray-700 dark:text-slate-300">
                Device type
                <div className="mt-1 flex gap-3">
                    {(['rear', 'front'] as const).map((r) => (
                        <label key={r} className="inline-flex items-center gap-1.5 cursor-pointer">
                            <input
                                type="radio"
                                name="fw-role"
                                value={r}
                                checked={role === r}
                                onChange={() => setRole(r)}
                                disabled={busy}
                                className="accent-blue-600"
                            />
                            <span className="capitalize">{r}</span>
                        </label>
                    ))}
                </div>
                <p className="mt-1 text-[11px] text-gray-500 dark:text-slate-400">
                    Stored under <code className="font-mono">{role}/</code> in the firmware bucket.
                </p>
            </label>

            <label className="block text-[11px] text-gray-700 dark:text-slate-300">
                Firmware version (stored on the S3 object; must match device telemetry)
                <input
                    type="text"
                    className="mt-1 w-full max-w-md rounded-md border border-gray-300 dark:border-slate-600 px-2 py-1.5 text-[11px] font-mono bg-white dark:bg-slate-900 disabled:opacity-50"
                    value={firmwareVersion}
                    onChange={(e) => setFirmwareVersion(e.target.value)}
                    disabled={busy}
                    placeholder="e.g. 2.4.1"
                    autoComplete="off"
                />
            </label>
        </div>
    );
}
